import React, { useContext, useState, useRef, useEffect, useLayoutEffect } from 'react';

import { gsap } from "gsap";

import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';

import { AuthContext } from "../context/AuthContext.jsx";
import { useApiContext } from '../context/ApiContext.jsx';

import Checkbox from '../components/Checkbox.jsx';
import RoomStatus from '../components/RoomStatus.jsx';
import Timer from '../components/Timer.jsx';
import { useScrambleText } from "../hooks/useScrambleText.jsx";
import { useSfx } from "../hooks/useSfx.jsx";

import ResultsPage from '../components/ResultsPage.jsx';
import CategoryInfo from '../components/CategoryInfo.jsx';

import { startDemoTimer } from '../services/RoomStatusService.js';

const GameContainer = () => {
	const { api, isDemo } = useApiContext();
	const { sessionInfo, setSessionInfo } = useContext(AuthContext);
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
	const [currentQuestion, setCurrentQuestion] = useState(null);
	const [currentGroup, setCurrentGroup] = useState(null);
	const [currentGroupInfoOpen, setCurrentGroupInfoOpen] = useState(false);
	const [questions, setQuestions] = useState([]);
	const [answerDone, setAnswerDone] = useState(false);
	const [selectedChoices, setSelectedChoices] = useState([]);
	const [isFinalStep, setIsFinalStep] = useState(false);
	const [timeLimit, setTimeLimit] = useState();
	const [shuffledChoices, setShuffledChoices] = useState([]);

	const cardRef = useRef(null);

	const sfx = useSfx();

	const { playSelect, playDeselect } = sfx;

	const prevIdRef = useRef(null);
	const groupRef = useRef(null);
	const questionRef = useRef(null);

	const maxNumberOfSelectableChoices = Number(currentQuestion?.maxNumberOfSelectableChoices ?? 1);
	const questionText = currentQuestion?.description ?? "";

	const demoTimerStartedRef = useRef(false);

	useScrambleText(questionRef, questionText, {
		durationOpts: { min: 0.6, max: 1.2, softCap: 260, sweetSpot: 40 },
		sfx: { playTypeTick: sfx.playTypeTick, playTypeEnd: sfx.playTypeEnd },
		sfxEnabled: !currentGroupInfoOpen,
		tickEvery: 2,
	});


	useEffect(() => {
		if (isDemo && !demoTimerStartedRef.current) {
			demoTimerStartedRef.current = true;
			console.log("startDemoTimer")
			startDemoTimer();
		}
	}, [isDemo]);

	useEffect(() => {
		if (!sessionInfo) return;
		const surveyQuestions = sessionInfo.groups.reduce((e, v) => {
			e.push(v.items);
			return e;
		}, []).flat()

		setQuestions(surveyQuestions)
		setCurrentQuestionIndex(sessionInfo.currentItemIndex || 0)
		if ((sessionInfo.currentItemIndex || 0) == surveyQuestions.length) setIsFinalStep(true)
	}, [sessionInfo]);

	useEffect(() => {
		if (!sessionInfo) return;
		if (!questions.length) return
		setCurrentQuestion(questions[currentQuestionIndex])

	}, [currentQuestionIndex, questions, sessionInfo]);

	useEffect(() => {
		if (!currentQuestion) return;
		setSelectedChoices(currentQuestion.inputs?.map((el) => el.choiceId))
	}, [currentQuestion]);

	useEffect(() => {
		if (!currentGroup) return;
		if (!currentQuestion) return;
		if (sessionInfo?.hideTopicCover) {
			setCurrentGroupInfoOpen(false);
			return;
		}
		setCurrentGroupInfoOpen(true)
	}, [currentGroup, sessionInfo?.hideTopicCover]);

	useEffect(() => {
		if (!currentQuestion) return;
		if (!currentQuestion.timeLimit) return;
		if (currentGroupInfoOpen) return;
		const time = new Date();
		time.setSeconds(time.getSeconds() + currentQuestion.timeLimit);
		setTimeLimit(time);
	}, [currentQuestion, currentGroupInfoOpen]);

	useEffect(() => {
		if (!currentQuestion) return;
		let currGroup = sessionInfo.groups.find((group) => {
			return group.id === currentQuestion.groupId;
		})
		setCurrentGroup(currGroup)
	}, [currentQuestion]);

	useEffect(() => {
		if (!currentQuestion?.id) return;
		if (currentGroupInfoOpen) return;

		const id = currentQuestion.id;

		const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
		const behavior = prefersReduced ? "auto" : "smooth";

		const header = document.querySelector(".app-header");
		const offset = header?.offsetHeight ?? 0;

		window.scrollTo({ top: 0, behavior });

		if (prevIdRef.current && prevIdRef.current !== id) {
			if (!currentGroupInfoOpen) {
				sfx.playSwoosh({ duration: 1.2 });
			}
		}
		prevIdRef.current = id;

	}, [currentQuestion?.id, currentGroupInfoOpen]);


	useEffect(() => {
		if (!currentQuestion) return;
		setAnswerDone(selectedChoices.length >= currentQuestion.minNumberOfSelectableChoices)
	}, [selectedChoices, currentQuestion]);

	useEffect(() => {
		if (!currentQuestion?.availableChoices) {
			setShuffledChoices([]);
			return;
		}

		const arr = [...currentQuestion.availableChoices];

		for (let i = arr.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[arr[i], arr[j]] = [arr[j], arr[i]];
		}

		setShuffledChoices(arr);
	}, [currentQuestion?.id]);

	const setChoice = (ev, el) => {
		ev?.stopPropagation?.();
		const id = el?.id ?? el;
		const isCheckboxEvent = typeof ev?.target?.checked === 'boolean';

		setSelectedChoices(prev => {
			const already = prev.includes(id);
			const wantSelect = isCheckboxEvent ? ev.target.checked : !already;

			if (wantSelect) {
				if (already) return prev;
				if (maxNumberOfSelectableChoices === 1) { playSelect(); return [id]; }
				if (prev.length >= maxNumberOfSelectableChoices) return prev;
				playSelect();
				return [...prev, id];
			} else {
				if (!already) return prev;
				playDeselect();
				return prev.filter(x => x !== id);
			}
		});
	};

	const handleAnswerSuccess = () => {
		setTimeLimit(null);

		setTimeout(() => {
			setQuestions(prev => {
				return prev.map((question) => {
					if (question.id === currentQuestion.id) {
						return {
							...question,
							inputs: selectedChoices.map(choiceId => ({ choiceId }))
						};
					}
					return question;
				});
			});

			setCurrentQuestionIndex(prevIndex => {
				const qIndex = prevIndex + 1;

				if (qIndex === questions.length) {
					setIsFinalStep(true);
					toast.dismiss();
					console.log("Risultati!!!");
				}

				return qIndex;
			});

			cardRef.current?.classList.remove('animate__bounceInRight');
			cardRef.current?.classList.remove('animate__bounceOutRight');
			cardRef.current?.classList.add('animate__bounceInLeft');
		}, 500);
	};

	const nextAnswer = (ev, el) => {
		if (ev) ev.preventDefault();
		ev.target.classList.add('disabled');

		cardRef.current?.classList.add('animate__bounceOutRight');

		if (isDemo) {
			handleAnswerSuccess();
			return;
		}

		api.post('/api/trivia/input', {
			itemId: currentQuestion.id,
			choices: selectedChoices.map(choiceId => ({ choiceId }))
		})
			.then(function (response) {
				handleAnswerSuccess();
			})
			.catch(function (error) {
				console.error('Errore salvataggio risposta', error);
				cardRef.current?.classList.remove('animate__bounceOutRight');
				cardRef.current?.classList.add('animate__bounceInRight');

				toast.error('Si è verificato un errore nel salvataggio della risposta.', {
					position: 'top-right',
					autoClose: 4000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					theme: 'colored',
				});
			});
	};

	const render = () => {

		if (isFinalStep || sessionInfo.sessionType > 1) {
			return <ResultsPage questions={questions} />
		}
		if (currentGroup && currentGroupInfoOpen == true && !sessionInfo?.hideTopicCover) {
			return <CategoryInfo setCurrentGroupInfoOpen={setCurrentGroupInfoOpen} currentGroup={currentGroup} />
		}

		return <>
			<RoomStatus questions={questions}></RoomStatus>
			{currentGroup && currentQuestion && (currentGroupInfoOpen == false) ? (
				<div className="game-content container-xl animate__animated animate__fadeIn animate__delay-1s">
					<div className="game-header d-flex flex-wrap flex-sm-nowrap justify-content-sm-between align-items-center text-center text-sm-start">

						<div className="mb-lg-2 mb-2 w-100">
							<h1 className="fw-semibold w-md-50 fs-3 fs-lg-2 lh-1 mb-0 my-2 me-md-3">{currentGroup.title}</h1>
						</div>

						<div className="mascotte-shadowed-container">
							{sessionInfo.template?.mascotNormalMediaId ?
								<img
									className="ms-3 mb-0 d-none d-sm-block mascotte mascotte-shadowed"
									src={`/api/trivia/media/${sessionInfo.template.mascotNormalMediaId}`}
									style={{ width: 65 }} />
								: ""}

							{/*<div className="ms-3 mascotte mascotte-shadowed mb-0 d-none d-sm-block"></div>*/}
						</div>

					</div>

					<div className="game-subheader d-flex flex-wrap flex-lg-nowrap justify-content-start align-items-start pb-4">
						<div className="text-lg-center order-2 order-lg-1 w-100 w-lg-auto mt-2 me-lg-4">
							<img src={`/img/react-app/avatars/01/avatar-${sessionInfo.currentAvatarId}.png`} style={{ width: 80, height: "auto" }} className="user-avatar user-avatar-main mx-auto d-none d-lg-block" />
							<div className="mt-2">
								<span>Domanda <strong>{currentQuestionIndex + 1}</strong> di <strong>{questions.length}</strong></span>
							</div>
							<div className="user-item-progress mt-1">
								<div className="user-item-progress-fragment user-item-progress-actual" style={{ width: `${(currentQuestionIndex * 100 / questions.length)}%` }}></div>
								<div className="user-item-progress-fragment user-item-progress-done" style={{ width: `${(currentQuestion.maxItemIndex * 100 / questions.length)}%` }}></div>
							</div>
						</div>

						<h3 className="fw-semibold align-self-center fs-4 order-1 order-lg-2 question-container">
							<div
								ref={questionRef}
								className="d-block fs-2 mt-lg-3"
								style={{ fontWeight: 600 }}
								aria-label="Testo domanda"
							>{currentQuestion.description}</div>
						</h3>

					</div>

					<div className="">
						<div ref={cardRef} className="card-question animate__animated">

							<small className={`question-info fs-5 mt-3 mx-auto ${currentQuestion.maxNumberOfSelectableChoices === 1 ? "question-info-hidden" : ""}`} style={{ width: "fit-content" }}>
								<i className="bi bi-info-circle me-2" style={{ animation: "rubberBandPause 2s ease-in-out infinite"}}></i>
								<span style={{ animation: "1.5s ease-out 0s infinite normal none running warn-pulse" }}>
									{currentQuestion.maxNumberOfSelectableChoices > 1 ? `Le risposte corrette a questa domanda sono più di una.` : ``}
									{currentQuestion.minNumberOfSelectableChoices > 1 ? (
										<span className="ms-1">Devi selezionare almeno {currentQuestion.minNumberOfSelectableChoices} risposte</span>
									) : ""}
								</span>
							</small>


							<div className="my-4 answers-grid pb-15">

								{shuffledChoices.map((el, index) => {
									return <div key={index} className="answer-text" onClick={(ev) => setChoice(ev, el)}>
										<div className="_form-check">
											<Checkbox
												key={index}
												value={el.id}
												isChecked={selectedChoices.includes(el.id)}
												__disabled={selectedChoices.length >= currentQuestion.maxNumberOfSelectableChoices && !selectedChoices.includes(el.id)}
												checkHandler={(ev) => setChoice(ev, el)}
												label={`<h4 class="mb-0 fs-3">${el.title}</h4>`}
												index={index}
											/>
										</div>
									</div>
								})}
							</div>
						</div>
						<div className="game-footer">
							<div className="game-footer-content d-flex justify-content-between">

								<button onClick={() => sfx.setMuted(m => !m)} className="btn-mute btn btn btn-primary btn-lg">
									{sfx.muted ? "🔇" : "🔊"}
								</button>

								<a onClick={(ev) => nextAnswer(ev, this)} id="nextButton" href="#" className={`btn btn-primary btn-lg px-sm-6 d-flex align-items-center ${!answerDone ? "disabled" : ""}`}>
									Avanti
								</a>

								<div className="" style={{ minHeight: 40 }}>
									{timeLimit && (
										<Timer
											className={`question-timer`}
											expiryTimestamp={timeLimit}
											onExpire={location.hostname == "localhost" ? null : nextAnswer}
											pauseTimer={currentGroupInfoOpen}
											warnCountdownFrom={5}
											warnSfxEnabled={!currentGroupInfoOpen && !sfx.muted}
											playWarn={sfx.playWarn}
											playTimeUp={sfx.playTimeUp}
											expireSfxEnabled={!currentGroupInfoOpen && !sfx.muted}
										/>
									)}
								</div>
							</div>
						</div>

					</div>


				</div>
			) : ""}
		</>
	}


	return (
		<>
			<div id={`game-container`}>
				{render()}
			</div>
		</>
	);
};

export default GameContainer;